import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import {
  BillingProcess,
  BillingProcessStatus,
} from 'src/app/models/billing-process';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { MessageUtil } from 'src/app/utils/message.util';

interface StatusFilterOption {
  label: string;
  value: BillingProcessStatus | null;
}

@Component({
  selector: 'app-billing-process-list',
  templateUrl: './billing-process-list.component.html',
  styleUrls: ['./billing-process-list.component.scss'],
})
export class BillingProcessListComponent implements OnInit {
  processes: BillingProcess[] = [];
  loading = false;
  selectedStatus: BillingProcessStatus | null = null;

  statusOptions: StatusFilterOption[] = [
    { label: 'Todos', value: null },
    { label: 'Em rascunho', value: 'DRAFT' },
    { label: 'Em execução', value: 'IN_PROGRESS' },
    { label: 'Concluídos', value: 'COMPLETED' },
    { label: 'Com falha', value: 'FAILED' },
    { label: 'Cancelados', value: 'CANCELED' },
  ];

  private readonly monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  constructor(
    private billingProcessService: BillingProcessService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.spinner.show();
    this.billingProcessService
      .list({ status: this.selectedStatus ?? undefined })
      .subscribe({
        next: (data) => {
          this.processes = data ?? [];
          this.loading = false;
          this.spinner.hide();
        },
        error: (err) => {
          this.loading = false;
          this.spinner.hide();
          this.messageService.generateMessage(
            'error',
            'SUMMARY.ERROR',
            err?.error?.message ?? 'Erro ao carregar processos de faturamento.',
          );
        },
      });
  }

  onChangeStatus(): void {
    this.load();
  }

  novo(): void {
    this.router.navigate(['/app/billing/billing-process/new']);
  }

  abrir(process: BillingProcess): void {
    this.router.navigate(['/app/billing/billing-process', process.id]);
  }

  editar(process: BillingProcess): void {
    this.router.navigate(['/app/billing/billing-process', process.id, 'edit']);
  }

  cancelar(process: BillingProcess): void {
    Swal.fire({
      title: 'Cancelar processo de faturamento',
      text: `Tem certeza que deseja cancelar o processo "${process.description}"? `
        + 'Todos os itens carregados serão removidos. Esta ação não pode ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Voltar',
      reverseButtons: true,
    }).then((result) => {
      if (!result.value) {
        return;
      }
      this.spinner.show();
      this.billingProcessService.cancel(process.id).subscribe({
        next: () => {
          this.spinner.hide();
          this.messageService.generateMessage(
            'success', 'SUMMARY.SUCCESS', 'Processo cancelado.');
          this.load();
        },
        error: (err) => {
          this.spinner.hide();
          this.messageService.generateMessage(
            'error', 'SUMMARY.ERROR',
            err?.error?.message ?? 'Erro ao cancelar processo.');
        },
      });
    });
  }

  formatPeriod(month: number, year: number): string {
    if (!month || !year) {
      return '';
    }
    return `${this.monthNames[month - 1]}/${year}`;
  }

  statusLabel(status: BillingProcessStatus): string {
    const option = this.statusOptions.find((o) => o.value === status);
    return option ? option.label : status;
  }

  statusBadgeClass(status: BillingProcessStatus): string {
    switch (status) {
      case 'DRAFT':
        return 'badge-draft';
      case 'IN_PROGRESS':
        return 'badge-progress';
      case 'COMPLETED':
        return 'badge-completed';
      case 'FAILED':
        return 'badge-failed';
      case 'CANCELED':
        return 'badge-canceled';
      default:
        return '';
    }
  }
}
